import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { showAppShellError } from '../startup/app-shell';
import { NotificationService } from './notification.service';
import { TranslationService } from './i18n/translation.service';

const RELOAD_AT_KEY = 'morichal.chunkReloadAt';
const RELOAD_WINDOW_MS = 60_000;

/**
 * Recovers from a stale cached app whose lazy route chunks no longer exist.
 *
 * A tab left open across a deploy asks for a hashed chunk the new build
 * removed. Without handling, that failure renders a blank page. The first
 * failure reloads once; a failure that repeats inside the window is reported
 * instead, so a genuinely missing chunk cannot reload forever.
 */
@Injectable({
  providedIn: 'root',
})
export class ChunkReloadService {
  private notificationService = inject(NotificationService);
  private translationService = inject(TranslationService);
  private document = inject(DOCUMENT);
  private handled = false;

  isChunkLoadError(error: unknown): boolean {
    const message = this.describe(error);
    return (
      message.includes('ChunkLoadError') ||
      message.includes('Failed to fetch dynamically imported module') ||
      (message.includes('Loading chunk') && message.includes('failed')) ||
      message.includes('Importing a module script failed') ||
      message.includes('error loading dynamically imported module') // Firefox
    );
  }

  handle(error: unknown): void {
    if (!this.isChunkLoadError(error) || this.handled) {
      return;
    }
    this.handled = true;

    try {
      const now = Date.now();
      const lastAttempt = Number(sessionStorage.getItem(RELOAD_AT_KEY) || 0);
      if (!lastAttempt || now - lastAttempt > RELOAD_WINDOW_MS) {
        sessionStorage.setItem(RELOAD_AT_KEY, String(now));
        window.location.reload();
        return;
      }
    } catch {
      // Storage blocked: without the guard a reload could loop, so report it instead
    }

    // On the first page the start-up shell is still up: it shows the error with Reload
    if (showAppShellError(this.document)) return;
    this.notificationService.error(
      this.translationService.t('app.updateAvailable'),
      this.translationService.t('app.updateAvailableMessage')
    );
  }

  private describe(error: unknown): string {
    if (!error) {
      return '';
    }
    if (typeof error === 'string') {
      return error;
    }
    if (error instanceof Error) {
      const cause = (error as Error & { cause?: unknown }).cause;
      return `${error.name} ${error.message} ${this.describe(cause)}`;
    }
    if (typeof error === 'object' && 'message' in error) {
      return String((error as { message: unknown }).message);
    }
    return String(error);
  }
}
